"use client";

import { createContext, type ReactNode, useEffect, useSyncExternalStore } from "react";

import type { AuthJwtClaims } from "@/gen/orval/webplatform/model/authJwtClaims.gen";

import {
  getMockClaimsServerSnapshot,
  getMockClaimsSnapshot,
  installMockClaimsConsoleHelpers,
  subscribeMockClaims,
} from "@/subsystems/access/permissions/dev/mock-claims-harness";

export const JwtContext = createContext<AuthJwtClaims | null>(null);

interface JwtProviderProps {
  children: ReactNode;
  decodedJwt: AuthJwtClaims | null;
}

export function JwtProvider({ children, decodedJwt }: JwtProviderProps) {
  // Mock claims subscribe to localStorage via useSyncExternalStore so
  // the SSR snapshot (always null) matches the first client render
  // before subscribers fire. The snapshot getters are no-ops in
  // production builds.
  const mockClaims = useSyncExternalStore(subscribeMockClaims, getMockClaimsSnapshot, getMockClaimsServerSnapshot);

  // Install dev console helpers (__setRbacMockClaims, __clearRbacMockClaims)
  // on the window object. The installer no-ops in production builds.
  useEffect(() => {
    installMockClaimsConsoleHelpers();
  }, []);

  // Spread mockClaims so only fields it explicitly defines override real
  // JWT fields — partial mocks (e.g., permissions only) preserve roles
  // from the real JWT.
  const value: AuthJwtClaims | null = mockClaims && decodedJwt ? { ...decodedJwt, ...mockClaims } : decodedJwt;

  return <JwtContext value={value}>{children}</JwtContext>;
}
